/**
 * Authentication API Service
 *
 * Handles all authentication-related API calls
 */

import { api } from '../client';

export const authService = {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{token: string, refreshToken: string, is_admin: boolean, userId: string, name: string, email: string, preferred_language: string}>}
   */
  async login(email, password) {
    const response = await api.post('/api/auth/login', { email, password });

    return {
      token: response.token,
      refreshToken: response.refresh_token,
      is_admin: response.is_admin,
      userId: response.id,
      name: response.name,
      email: email,
      preferred_language: response.preferred_language || 'en',
    };
  },

  /**
   * Revoke the refresh token server-side (real logout). Best-effort: never
   * throws — the caller clears local state regardless. No-op without a token.
   * @param {string} [refreshToken]
   */
  async logout(refreshToken) {
    if (!refreshToken) return;
    try {
      await api.post('/api/auth/logout', { refresh_token: refreshToken });
    } catch {
      // Already-revoked / network error — logout still proceeds locally.
    }
  },

  /**
   * Change the current user's password. Returns the freshly-issued token
   * so the caller can replace the old one in localStorage / context.
   * Other open sessions for the same user are invalidated server-side.
   *
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{token: string, refresh_token: string, id: string, name: string, is_admin: boolean, preferred_language: string}>}
   */
  async changePassword(currentPassword, newPassword) {
    return await api.post('/api/user/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
};
